import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDateFormat } from './DateFormatContext';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import Icon from './Icons';
import { photoUrl } from './photoUtils';
import { useGrowingMethods, GrowingMethodSubstrateSelect } from './GrowingMethodPicker';
import { fieldOptionList, useFieldOptions } from './fieldOptions';
import { usePullToRefresh } from './hooks';
import ErrorScreen from './ErrorScreen';
import { useGuidance } from './GuidanceContext';
import GuidanceSheet from './GuidanceSheet';
import { PLAN_GUIDANCE_TITLE, PlanGuidanceBody } from './guidanceContent';
import { plantDisplayName } from './nameUtils';

// ─── Constants ───────────────────────────────────────────────────────────────

const TASK_TYPES = [
  { value: 'health_check',     label: 'Health Check',       icon: 'clipboardCheck' },
  { value: 'watering',         label: 'Watering',           icon: 'leaf' },
  { value: 'fertilize',        label: 'Fertilize',          icon: 'leaf' },
  { value: 'repot',            label: 'Repot',              icon: 'amphora' },
  { value: 'treatment',        label: 'Treatment',          icon: 'briefcaseMedical' },
  { value: 'pruning',          label: 'Pruning',            icon: 'scissors' },
  { value: 'staking_training', label: 'Staking / Training', icon: 'dumbbell' },
  { value: 'substrate_change', label: 'Substrate Change',   icon: 'stone' },
  { value: 'measurement',      label: 'Measurement',        icon: 'ruler' },
  { value: 'general_note',     label: 'General Note',       icon: 'pencil' },
];

const TASK_TYPE_MAP = Object.fromEntries(TASK_TYPES.map(t => [t.value, t]));

const PLAN_THEMES = [
  { key: 'none',           label: 'Most Overdue',      description: "Plants needing attention most urgently",                     icon: 'clock',          hasSubFilter: false },
  { key: 'newest',         label: 'Newest Arrivals',   description: 'Recently acquired plants needing checking or potting',       icon: 'leaf',           hasSubFilter: false },
  { key: 'location',       label: 'By Location',       description: 'Plan care for one room or space at a time',                  icon: 'mapPin',         hasSubFilter: true  },
  { key: 'growing_method', label: 'By Growing Method', description: 'Soil, semi-hydro, or water plants together',                 icon: 'stone',          hasSubFilter: true  },
  { key: 'tag',            label: 'By Tag',             description: 'Focus on a custom group like Pest Watch or Rare',           icon: 'tag',            hasSubFilter: true  },
  { key: 'status',         label: 'By Status',         description: 'Review propagations, available plants, or another status',   icon: 'clipboardCheck', hasSubFilter: true  },
  { key: 'random',         label: 'Surprise Me',       description: 'Random spread of plants from your collection',               icon: 'dice5',          hasSubFilter: false },
  { key: 'custom',         label: 'Specific Plants',   description: 'Hand-pick individual plants for a targeted task',            icon: 'search',         hasSubFilter: false, isCustom: true },
];

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ─── Date helpers ─────────────────────────────────────────────────────────────

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d;
}

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function getDateQuickOptions() {
  const today = new Date();
  const day = today.getDay(); // 0=Sun, 6=Sat
  // This weekend: next Sat-Sun (or today if Sat, tomorrow if Sun)
  const daysToSat = day === 6 ? 0 : day === 0 ? -1 : 6 - day;
  const thisSat = addDays(today, daysToSat);
  const thisSun = addDays(thisSat, 1);
  // Next weekend: following Sat-Sun
  const nextSat = addDays(thisSat, 7);
  const nextSun = addDays(nextSat, 1);
  // This week: today through this Sunday
  const daysToSun = day === 0 ? 0 : 7 - day;
  const thisSunEow = addDays(today, daysToSun);
  // Next week: Mon–Sun
  const daysToNextMon = day === 0 ? 1 : 8 - day;
  const nextMon = addDays(today, daysToNextMon);
  const nextSunEow = addDays(nextMon, 6);

  const opts = [
    { label: 'Today', start: today, end: today },
    { label: 'Tomorrow', start: addDays(today, 1), end: addDays(today, 1) },
  ];
  if (day !== 6 && day !== 0) {
    opts.push({ label: 'This Weekend', start: thisSat, end: thisSun });
  } else if (day === 6) {
    opts.push({ label: 'This Weekend', start: thisSat, end: thisSun });
  }
  opts.push({ label: 'Next Weekend', start: nextSat, end: nextSun });
  opts.push({ label: 'Rest of This Week', start: today, end: thisSunEow });
  opts.push({ label: 'Next Week', start: nextMon, end: nextSunEow });
  return opts.map(o => ({ ...o, start: formatDate(o.start), end: formatDate(o.end) }));
}

function formatDisplayDate(isoDate) {
  if (!isoDate) return '';
  const [, m, d] = isoDate.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
}

// ─── Task type pill ───────────────────────────────────────────────────────────

function TaskTypePill({ taskType, size = 14 }) {
  const tt = TASK_TYPE_MAP[taskType] || { label: taskType, icon: 'clipboardCheck' };
  return (
    <span className={`plan-type-pill plan-type-pill--${taskType}`}>
      <Icon name={tt.icon} size={size} />
      {tt.label}
    </span>
  );
}

// ─── Assignee chip ────────────────────────────────────────────────────────────

function AssigneeChip({ assignee }) {
  if (!assignee) return null;
  const full = assignee.display_name || assignee.email || '?';
  const firstName = full.split(' ')[0];
  return (
    <span className="plan-assignee" title={full}>
      {firstName}
    </span>
  );
}

// ─── Cycle progress dots ──────────────────────────────────────────────────────

function CycleDots({ position, total }) {
  if (!total || !position) return null;
  return (
    <span className="plan-cycle-dots">
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`plan-cycle-dot ${i < position ? 'plan-cycle-dot--done' : ''}`}
        />
      ))}
      <span className="plan-cycle-label">{position}/{total}</span>
    </span>
  );
}

// ─── Reschedule chips ────────────────────────────────────────────────────────

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getRescheduleChips() {
  const today = new Date();
  const chips = [];
  for (let i = 1; i <= 6; i++) {
    const d = addDays(today, i);
    chips.push({ label: i === 1 ? 'Tomorrow' : DAY_SHORT[d.getDay()], date: formatDate(d) });
  }
  // Show "Next Mon" only when Monday isn't already in the 6-day window (Mon–Wed today)
  const daysToNextMon = (8 - today.getDay()) % 7 || 7;
  if (daysToNextMon > 4) {
    chips.push({ label: 'Next Mon', date: formatDate(addDays(today, daysToNextMon)) });
  }
  return chips;
}

function RescheduleChips({ onPick, onCancel }) {
  return (
    <div className="plan-reschedule-wrap">
      <span className="plan-reschedule-label">Reschedule to</span>
      <div className="plan-reschedule-chips">
        {getRescheduleChips().map(c => (
          <button key={c.date} className="plan-reschedule-chip" onClick={() => onPick(c.date)}>
            {c.label}
          </button>
        ))}
      </div>
      <button className="plan-reschedule-cancel" onClick={onCancel}>Cancel</button>
    </div>
  );
}

// ─── Task Card ────────────────────────────────────────────────────────────────

function formatAcquiredLabel(isoDate) {
  if (!isoDate) return null;
  const [, m, d] = isoDate.split('-');
  return `Acquired ${MONTH_NAMES[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
}

function TaskCard({ task, selectMode, isSelected, onToggleSelect, onSkip, onLog, onDelete, onReschedule, onUnmark, members = [], onReassign, onChangeTaskType }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [changingType, setChangingType] = useState(false);
  const [unmarkMenuOpen, setUnmarkMenuOpen] = useState(false);
  const moreWrapRef = useRef(null);
  const { formatDate } = useDateFormat();
  const unmarkWrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleOutside(e) {
      if (moreWrapRef.current && !moreWrapRef.current.contains(e.target)) {
        setMenuOpen(false); setRescheduling(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!unmarkMenuOpen) return;
    function handleOutside(e) {
      if (unmarkWrapRef.current && !unmarkWrapRef.current.contains(e.target)) {
        setUnmarkMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [unmarkMenuOpen]);
  const isDone     = task.status === 'done';
  const isSkipped  = task.status === 'skipped';
  const isFinished = isDone || isSkipped;

  const acquiredLabel = formatAcquiredLabel(task.acquisition_date);
  const isRecentArrival = (() => {
    if (!task.acquisition_date) return false;
    const acq = new Date(task.acquisition_date);
    const now = new Date();
    return (now - acq) / (1000 * 60 * 60 * 24) <= 60;
  })();

  return (
    <div
      className={`plan-task-card ${task.status === 'overdue' ? 'plan-task-card--overdue' : ''} ${isFinished ? 'plan-task-card--done' : ''}`}
      onClick={selectMode && !isFinished ? () => onToggleSelect(task.id) : undefined}
    >
      {/* Photo thumbnail */}
      <button
        className="plan-task-thumb-link"
        onClick={e => { e.stopPropagation(); navigate(`/plants/${task.plant_id}`); }}
        tabIndex={-1}
        aria-hidden="true"
        type="button"
      >
        {task.cover_photo_id ? (
          <img
            src={photoUrl(task.cover_photo_id, task.cover_photo_rotation_version, task.cover_photo_created_at)}
            alt=""
            className="plan-task-thumb"
            style={{ objectPosition: `${task.cover_photo_offset_x ?? 50}% ${task.cover_photo_offset_y ?? 50}%` }}
          />
        ) : (
          <div className="plan-task-thumb plan-task-thumb--empty">
            <Icon name="leaf" size={18} />
          </div>
        )}
      </button>

      {selectMode && !isFinished && (
        <label className="plan-task-checkbox" onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(task.id)}
          />
        </label>
      )}

      <div className="plan-task-body">
        <div className="plan-task-top">
          <button
            className="plan-task-plant"
            type="button"
            onClick={e => { e.stopPropagation(); navigate(`/plants/${task.plant_id}`); }}
          >
            {task.display_name}
            <span className="plan-task-specimen"> · {task.specimen_id}</span>
          </button>
          {task.status === 'overdue' && <span className="plan-task-overdue-badge">Overdue</span>}
          {task.has_scheduled_today_conflict && <span className="plan-task-conflict-badge">Planned today</span>}
        </div>

        {/* Location + growing method context */}
        <div className="plan-task-location-row">
          {task.locations && task.locations.map(loc => (
            <span key={loc} className="plan-task-loc-chip">
              <Icon name="mapPin" size={11} /> {loc}
            </span>
          ))}
          {task.growing_method && (
            <span className="plan-task-loc-chip plan-task-loc-chip--method">
              {task.growing_method}
            </span>
          )}
          {isRecentArrival && acquiredLabel && (
            <span className="plan-task-loc-chip plan-task-loc-chip--new">{acquiredLabel}</span>
          )}
        </div>

        <div className="plan-task-meta">
          <TaskTypePill taskType={task.task_type} />
          {task.recurrence_type === 'cycles' && (
            <CycleDots position={task.cycle_position} total={task.total_cycles} />
          )}
          <AssigneeChip assignee={task.assignee} />
          {task.days_since != null && (
            <span className="plan-task-days">{task.days_since}d since last care</span>
          )}
        </div>

        {task.notes && <p className="plan-task-notes">{task.notes}</p>}

        {isFinished ? (
          <div className="plan-task-completed-row" onClick={e => e.stopPropagation()}>
            <p className="plan-task-completed-label">
              {isDone ? '✓ Done' : '— Skipped'}
              {task.completed_at ? ` · ${formatDate(task.completed_at)}` : ''}
            </p>
            {onUnmark && (
              <div className="plan-task-more" ref={unmarkWrapRef}>
                <button
                  className="btn-icon plan-task-more-btn"
                  onClick={() => setUnmarkMenuOpen(o => !o)}
                  aria-label="More options"
                  title="More options"
                >
                  ···
                </button>
                {unmarkMenuOpen && (
                  <div className="plan-more-menu">
                    <button
                      className="plan-more-item"
                      onClick={() => { setUnmarkMenuOpen(false); onUnmark(task); }}
                    >
                      <Icon name="rotateCcw" size={14} /> Unmark complete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : !selectMode && (
          <div className="plan-task-actions" onClick={e => e.stopPropagation()}>
            <button className="btn btn-sm btn-secondary plan-task-btn" onClick={() => onLog(task)}>
              Log
            </button>
            <button className="btn btn-sm btn-ghost plan-task-btn" onClick={() => onSkip(task)}>
              Skip
            </button>
            <div className="plan-task-more" ref={moreWrapRef}>
              <button
                className="btn-icon plan-task-more-btn"
                onClick={() => { setMenuOpen(o => !o); setRescheduling(false); setReassigning(false); setChangingType(false); }}
                aria-label="More options"
                title="More options"
              >
                ···
              </button>
              {menuOpen && (
                <div className="plan-more-menu">
                  {!rescheduling && !reassigning && !changingType ? (
                    <>
                      <button className="plan-more-item" onClick={() => setRescheduling(true)}>
                        <Icon name="rotateCw" size={14} /> Reschedule
                      </button>
                      <button className="plan-more-item" onClick={() => setReassigning(true)}>
                        <Icon name="userCircle" size={14} /> Reassign
                      </button>
                      <button className="plan-more-item" onClick={() => setChangingType(true)}>
                        <Icon name="clipboardCheck" size={14} /> Change type
                      </button>
                      <button
                        className="plan-more-item plan-more-item--danger"
                        onClick={() => { setMenuOpen(false); onDelete(task); }}
                      >
                        <Icon name="trash" size={14} /> Delete task
                      </button>
                    </>
                  ) : rescheduling ? (
                    <RescheduleChips
                      onPick={date => { setMenuOpen(false); setRescheduling(false); onReschedule(task.id, date); }}
                      onCancel={() => setRescheduling(false)}
                    />
                  ) : reassigning ? (
                    <div className="plan-reassign-picker">
                      <span className="plan-reschedule-label">Assign to</span>
                      <div className="plan-reschedule-chips">
                        {members.map(m => (
                          <button
                            key={m.id}
                            className={`plan-reschedule-chip${task.assigned_to_user_id === m.id ? ' plan-reschedule-chip--active' : ''}`}
                            onClick={() => { setMenuOpen(false); setReassigning(false); onReassign(task.id, m.id); }}
                          >
                            {(m.display_name || m.email || '?').split(' ')[0]}
                          </button>
                        ))}
                        <button
                          className={`plan-reschedule-chip${!task.assigned_to_user_id ? ' plan-reschedule-chip--active' : ''}`}
                          onClick={() => { setMenuOpen(false); setReassigning(false); onReassign(task.id, null); }}
                        >
                          Unassigned
                        </button>
                      </div>
                      <button className="plan-reschedule-cancel" onClick={() => setReassigning(false)}>Cancel</button>
                    </div>
                  ) : (
                    <div className="plan-reassign-picker">
                      <span className="plan-reschedule-label">Task type</span>
                      <div className="plan-reschedule-chips">
                        {TASK_TYPES.map(tt => (
                          <button
                            key={tt.value}
                            className={`plan-reschedule-chip${task.task_type === tt.value ? ' plan-reschedule-chip--active' : ''}`}
                            onClick={() => { setMenuOpen(false); setChangingType(false); onChangeTaskType(task.id, tt.value); }}
                          >
                            {tt.label}
                          </button>
                        ))}
                      </div>
                      <button className="plan-reschedule-cancel" onClick={() => setChangingType(false)}>Cancel</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Day accordion ────────────────────────────────────────────────────────────

function DaySection({ date, label, tasks, isOpen, onToggle, selectMode, selectedIds, onToggleSelect, onSkip, onLog, onDelete, onCompleteAll, onClearDay, onRescheduleDay, onReschedule, onUnmark, members, onReassign, onChangeTaskType, onReassignDay, onChangeTypeDay }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [reschedulingDay, setReschedulingDay] = useState(false);
  const [assigningDay, setAssigningDay] = useState(false);
  const [changingTypeDay, setChangingTypeDay] = useState(false);
  const menuWrapRef = useRef(null);
  const pendingTasks = tasks.filter(t => t.status === 'pending' || t.status === 'overdue');
  const allDone = tasks.length > 0 && pendingTasks.length === 0;

  function closeMenu() { setMenuOpen(false); setConfirmClear(false); setReschedulingDay(false); setAssigningDay(false); setChangingTypeDay(false); }

  useEffect(() => {
    if (!menuOpen) return;
    function handleOutside(e) {
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target)) closeMenu();
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [menuOpen]);

  return (
    <div className="plan-day-section" data-date={date}>
      <div className="plan-day-header-row">
        <button
          className={`plan-day-header${allDone ? ' plan-day-header--all-done' : ''}`}
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          <span className="plan-day-label">{label}</span>
          <span className="plan-day-date">{formatDisplayDate(date)}</span>
          <span className="plan-day-count">{tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
          {allDone && <Icon name="check" size={14} className="plan-day-done-check" />}
          <Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={16} />
        </button>

        <div className="plan-day-header-actions">
          {pendingTasks.length > 0 && !selectMode && (
            <button
              className="btn btn-sm btn-ghost plan-complete-all-btn"
              onClick={e => { e.stopPropagation(); onCompleteAll(pendingTasks); }}
            >
              Complete All
            </button>
          )}
          <div className="plan-day-more-wrap" ref={menuWrapRef}>
            <button
              className="btn-icon plan-day-more-btn"
              onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }}
              aria-label="Day options"
              title="Day options"
            >
              ···
            </button>
            {menuOpen && (
              <div className="plan-day-menu">
                {pendingTasks.length === 0 && !reschedulingDay && !assigningDay && !changingTypeDay && !confirmClear && (
                  <span className="plan-day-menu-empty">No pending tasks</span>
                )}
                {pendingTasks.length > 0 && !reschedulingDay && !assigningDay && !changingTypeDay && !confirmClear && (
                  <>
                    <button
                      className="plan-day-menu-item"
                      onClick={e => { e.stopPropagation(); setReschedulingDay(true); }}
                    >
                      <Icon name="rotateCw" size={14} /> Reschedule all
                    </button>
                    <button
                      className="plan-day-menu-item"
                      onClick={e => { e.stopPropagation(); setAssigningDay(true); }}
                    >
                      <Icon name="userCircle" size={14} /> Assign all
                    </button>
                    <button
                      className="plan-day-menu-item"
                      onClick={e => { e.stopPropagation(); setChangingTypeDay(true); }}
                    >
                      <Icon name="clipboardCheck" size={14} /> Change all types
                    </button>
                    <button
                      className="plan-day-menu-item plan-day-menu-item--danger"
                      onClick={e => { e.stopPropagation(); setConfirmClear(true); }}
                    >
                      <Icon name="trash" size={14} /> Clear pending tasks
                    </button>
                  </>
                )}
                {reschedulingDay && (
                  <RescheduleChips
                    onPick={newDate => { closeMenu(); onRescheduleDay(date, newDate); }}
                    onCancel={() => setReschedulingDay(false)}
                  />
                )}
                {assigningDay && (
                  <div className="plan-reassign-picker">
                    <span className="plan-reschedule-label">Assign all to</span>
                    <div className="plan-reschedule-chips">
                      {members.map(m => (
                        <button
                          key={m.id}
                          className="plan-reschedule-chip"
                          onClick={() => { closeMenu(); onReassignDay(date, m.id); }}
                        >
                          {(m.display_name || m.email || '?').split(' ')[0]}
                        </button>
                      ))}
                      <button
                        className="plan-reschedule-chip"
                        onClick={() => { closeMenu(); onReassignDay(date, null); }}
                      >
                        Unassigned
                      </button>
                    </div>
                    <button className="plan-reschedule-cancel" onClick={() => setAssigningDay(false)}>Cancel</button>
                  </div>
                )}
                {changingTypeDay && (
                  <div className="plan-reassign-picker">
                    <span className="plan-reschedule-label">Change all to</span>
                    <div className="plan-reschedule-chips">
                      {TASK_TYPES.map(tt => (
                        <button
                          key={tt.value}
                          className="plan-reschedule-chip"
                          onClick={() => { closeMenu(); onChangeTypeDay(date, tt.value); }}
                        >
                          {tt.label}
                        </button>
                      ))}
                    </div>
                    <button className="plan-reschedule-cancel" onClick={() => setChangingTypeDay(false)}>Cancel</button>
                  </div>
                )}
                {confirmClear && (
                  <>
                    <span className="plan-day-menu-confirm-text">Clear {pendingTasks.length} pending?</span>
                    <button
                      className="plan-day-menu-item plan-day-menu-item--danger"
                      onClick={() => { closeMenu(); onClearDay(date); }}
                    >
                      Yes, clear
                    </button>
                    <button className="plan-day-menu-item" onClick={() => setConfirmClear(false)}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="plan-day-tasks">
          {tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              selectMode={selectMode}
              isSelected={selectedIds.has(task.id)}
              onToggleSelect={onToggleSelect}
              onSkip={onSkip}
              onLog={onLog}
              onDelete={onDelete}
              onReschedule={onReschedule}
              onUnmark={onUnmark}
              members={members}
              onReassign={onReassign}
              onChangeTaskType={onChangeTaskType}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Overdue section ──────────────────────────────────────────────────────────

function OverdueSection({ tasks, isOpen, onToggle, selectMode, selectedIds, onToggleSelect, onSkip, onLog, onDelete, onCompleteAll, onReschedule, onRescheduleOverdue, onClearOverdue, onUnmark, members, onReassign, onChangeTaskType }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const menuWrapRef = useRef(null);
  const pendingTasks = tasks.filter(t => t.status !== 'done' && t.status !== 'skipped');

  function closeMenu() { setMenuOpen(false); setRescheduling(false); setConfirmClear(false); }

  useEffect(() => {
    if (!menuOpen) return;
    function handleOutside(e) {
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target)) closeMenu();
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [menuOpen]);

  return (
    <div className="plan-overdue-section">
      <div className="plan-day-header-row">
        <button className="plan-day-header plan-day-header--overdue" onClick={onToggle}>
          <span className="plan-day-label">⚠ Overdue</span>
          <span className="plan-day-count">{tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
          <Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={16} />
        </button>
        <div className="plan-day-header-actions">
          {pendingTasks.length > 0 && !selectMode && (
            <button
              className="btn btn-sm btn-ghost plan-complete-all-btn"
              onClick={e => { e.stopPropagation(); onCompleteAll(pendingTasks); }}
            >
              Complete All
            </button>
          )}
          <div className="plan-day-more-wrap" ref={menuWrapRef}>
            <button
              className="btn-icon plan-day-more-btn"
              onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); setRescheduling(false); setConfirmClear(false); }}
              aria-label="Overdue options"
              title="Overdue options"
            >
              ···
            </button>
            {menuOpen && (
              <div className="plan-day-menu">
                {pendingTasks.length === 0 && !rescheduling && !confirmClear && (
                  <span className="plan-day-menu-empty">No pending tasks</span>
                )}
                {pendingTasks.length > 0 && !rescheduling && !confirmClear && (
                  <>
                    <button
                      className="plan-day-menu-item"
                      onClick={e => { e.stopPropagation(); setRescheduling(true); }}
                    >
                      <Icon name="rotateCw" size={14} /> Reschedule overdue
                    </button>
                    <button
                      className="plan-day-menu-item plan-day-menu-item--danger"
                      onClick={e => { e.stopPropagation(); setConfirmClear(true); }}
                    >
                      <Icon name="trash" size={14} /> Clear overdue
                    </button>
                  </>
                )}
                {rescheduling && (
                  <RescheduleChips
                    onPick={date => { closeMenu(); onRescheduleOverdue(date); }}
                    onCancel={() => setRescheduling(false)}
                  />
                )}
                {confirmClear && (
                  <>
                    <span className="plan-day-menu-confirm-text">Clear all {pendingTasks.length} overdue?</span>
                    <button
                      className="plan-day-menu-item plan-day-menu-item--danger"
                      onClick={() => { closeMenu(); onClearOverdue(); }}
                    >
                      Yes, clear
                    </button>
                    <button className="plan-day-menu-item" onClick={() => setConfirmClear(false)}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="plan-day-tasks">
          {tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              selectMode={selectMode}
              isSelected={selectedIds.has(task.id)}
              onToggleSelect={onToggleSelect}
              onSkip={onSkip}
              onLog={onLog}
              onDelete={onDelete}
              onReschedule={onReschedule}
              onUnmark={onUnmark}
              members={members}
              onReassign={onReassign}
              onChangeTaskType={onChangeTaskType}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Log task modal ───────────────────────────────────────────────────────────

function LogModal({ task, onSave, onCancel, saving }) {
  const today = formatDate(new Date());
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [detail, setDetail] = useState({});
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const growingMethods = useGrowingMethods();
  const fieldOptions = useFieldOptions();

  const taskType = task?.task_type;
  const potTypeOptions = fieldOptionList(fieldOptions, 'repot_pot_type');
  const rootConditionOptions = fieldOptionList(fieldOptions, 'repot_root_condition');
  const fertilizeMethodOptions = fieldOptionList(fieldOptions, 'fertilize_method');
  const treatmentCategoryOptions = fieldOptionList(fieldOptions, 'treatment_category');
  const treatmentMethodOptions = fieldOptionList(fieldOptions, 'treatment_method');

  useEffect(() => {
    if (taskType === 'substrate_change') {
      setDetail(d => ({
        ...d,
        ...(task?.growing_method ? { old_growing_method: task.growing_method } : {}),
        ...(task?.substrate      ? { old_substrate:      task.substrate }      : {}),
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskType]);

  function df(key, val) { setDetail(d => ({ ...d, [key]: val })); }
  function dv(key) { return detail[key] ?? ''; }

  function handlePhotoUpload(file) {
    const fd = new FormData();
    fd.append('file', file);
    fetch(`/plants/${task?.plant_id}/photos`, { method: 'POST', body: fd })
      .then(r => { if (!r.ok) throw new Error(); setPhotoUploaded(true); })
      .catch(() => setPhotoError('Photo upload failed'));
  }

  function buildPayload() {
    const base = { date, notes };
    if (taskType === 'measurement') {
      return {
        ...base,
        height_inches: dv('height') ? parseFloat(dv('height')) : null,
        longest_leaf_inches: dv('longest_leaf') ? parseFloat(dv('longest_leaf')) : null,
        leaf_count: dv('leaf_count') ? parseInt(dv('leaf_count'), 10) : null,
      };
    }
    if (taskType === 'repot') {
      return { ...base, repot: {
        new_pot_size: dv('new_pot_size') ? parseFloat(dv('new_pot_size')) : null,
        pot_type: dv('pot_type') || null,
        new_growing_method: dv('new_growing_method') || null,
        new_substrate: dv('new_substrate') || null,
        root_condition: dv('root_condition') || null,
      }};
    }
    if (taskType === 'fertilize') {
      return { ...base, fertilize: {
        product: dv('product') || null,
        concentration: dv('concentration') || null,
        method: dv('method') || null,
      }};
    }
    if (taskType === 'treatment') {
      return { ...base, treatment: {
        treatment_category: dv('treatment_category') || null,
        target: dv('target') || null,
        product: dv('product') || null,
        method: dv('method') || null,
      }};
    }
    if (taskType === 'substrate_change') {
      return { ...base, substrate_change: {
        old_growing_method: dv('old_growing_method') || null,
        old_substrate: dv('old_substrate') || null,
        new_growing_method: dv('new_growing_method') || null,
        new_substrate: dv('new_substrate') || null,
        reason: dv('reason') || null,
      }};
    }
    if (taskType === 'acclimation') {
      return { ...base, acclimation: {
        method: dv('method') || null,
        source_condition: dv('source_condition') || null,
        start_date: dv('start_date') || null,
        end_date: dv('end_date') || null,
      }};
    }
    return base;
  }

  function field(label, keyName, type = 'text', placeholder = '', inputProps = {}) {
    return (
      <div key={keyName} className="form-field">
        <label className="form-label">{label}</label>
        <input type={type} className="form-input" value={dv(keyName)}
          onChange={e => df(keyName, e.target.value)} placeholder={placeholder} {...inputProps} />
      </div>
    );
  }

  return createPortal(
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Log: {task?.display_name}</h2>
          <button type="button" className="btn-icon" onClick={onCancel} aria-label="Close" title="Close"><Icon name="x" size={18} /></button>
        </div>

        <TaskTypePill taskType={taskType} />

        <div className="form-field" style={{ marginTop: '1rem' }}>
          <label className="form-label">Date</label>
          <input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)} />
        </div>

        {taskType === 'measurement' && (
          <>
            {field('Height (in)', 'height', 'number', '0.0')}
            {field('Longest Leaf (in)', 'longest_leaf', 'number', '0.0')}
            {field('Leaf Count', 'leaf_count', 'number', '0')}
          </>
        )}

        {taskType === 'repot' && (
          <>
            {field('New Pot Size (in)', 'new_pot_size', 'number', 'e.g. 4', { min: '0', step: '0.5', inputMode: 'decimal' })}
            <div className="form-field">
              <label className="form-label">Pot Type</label>
              <select className="form-input" value={dv('pot_type')} onChange={e => df('pot_type', e.target.value)}>
                <option value="">— Select —</option>
                {potTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <GrowingMethodSubstrateSelect
              methods={growingMethods}
              methodValue={dv('new_growing_method')}
              substrateValue={dv('new_substrate')}
              onMethodChange={v => df('new_growing_method', v)}
              onSubstrateChange={v => df('new_substrate', v)}
              methodLabel="New Growing Method"
              substrateLabel="New Substrate"
            />
            <div className="form-field">
              <label className="form-label">Root Condition</label>
              <select className="form-input" value={dv('root_condition')} onChange={e => df('root_condition', e.target.value)}>
                <option value="">— Select —</option>
                {rootConditionOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {taskType === 'fertilize' && (
          <>
            {field('Product', 'product', 'text', 'e.g. Osmocote')}
            {field('Concentration', 'concentration', 'text', 'e.g. half strength')}
            <div className="form-field">
              <label className="form-label">Method</label>
              <select className="form-input" value={dv('method')} onChange={e => df('method', e.target.value)}>
                <option value="">— Select —</option>
                {fertilizeMethodOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {taskType === 'treatment' && (
          <>
            <div key="treatment_category" className="form-field">
              <label className="form-label">Category</label>
              <select className="form-input" value={dv('treatment_category')} onChange={e => df('treatment_category', e.target.value)}>
                <option value="">— select —</option>
                {treatmentCategoryOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            {field('Target', 'target', 'text', 'e.g. spider mites, fungus gnats')}
            {field('Product', 'product', 'text', 'e.g. neem oil, spinosad')}
            <div className="form-field">
              <label className="form-label">Method</label>
              <select className="form-input" value={dv('method')} onChange={e => df('method', e.target.value)}>
                <option value="">— Select —</option>
                {treatmentMethodOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {taskType === 'substrate_change' && (
          <>
            <GrowingMethodSubstrateSelect
              methods={growingMethods}
              methodValue={dv('old_growing_method')}
              substrateValue={dv('old_substrate')}
              onMethodChange={v => df('old_growing_method', v)}
              onSubstrateChange={v => df('old_substrate', v)}
              methodLabel="Old Growing Method"
              substrateLabel="Old Substrate"
            />
            <GrowingMethodSubstrateSelect
              methods={growingMethods}
              methodValue={dv('new_growing_method')}
              substrateValue={dv('new_substrate')}
              onMethodChange={v => df('new_growing_method', v)}
              onSubstrateChange={v => df('new_substrate', v)}
              methodLabel="New Growing Method"
              substrateLabel="New Substrate"
            />
            {field('Reason', 'reason', 'text', 'e.g. overwatering risk')}
          </>
        )}

        {taskType === 'acclimation' && (
          <>
            {field('Method', 'method', 'text', 'e.g. humidity tent')}
            {field('Source Condition', 'source_condition', 'text', 'e.g. greenhouse')}
            <div key="start_date" className="form-field">
              <label className="form-label">Start Date</label>
              <input type="date" className="form-input" value={dv('start_date')} onChange={e => df('start_date', e.target.value)} />
            </div>
            <div key="end_date" className="form-field">
              <label className="form-label">End Date</label>
              <input type="date" className="form-input" value={dv('end_date')} onChange={e => df('end_date', e.target.value)} />
            </div>
          </>
        )}

        <div className="form-field">
          <label className="form-label">Notes</label>
          <textarea className="form-input" rows={3} value={notes}
            onChange={e => setNotes(e.target.value)} placeholder="Any observations…" />
        </div>

        <div className="form-field">
          <label className="form-label">Photo</label>
          <label className="plan-log-photo-label">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={e => e.target.files[0] && handlePhotoUpload(e.target.files[0])}
            />
            <Icon name="camera" size={15} />
            {photoUploaded ? ' Photo added ✓' : ' Add photo'}
          </label>
          {photoError && <p style={{ color: 'var(--danger, #c0392b)', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>{photoError}</p>}
        </div>

        <div className="modal-actions">
          <button className="btn btn-primary" onClick={() => onSave(buildPayload())} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Session breadcrumb ────────────────────────────────────────────────────────

const STEP_LABELS = {
  theme:     'Group',
  plantpick: 'Plants',
  tasktype:  'Task',
  subfilter: 'Filter',
  datepick:  'When',
  daterange: 'When',
};

function SessionBreadcrumb({ history, current, onJump }) {
  const seen = new Set();
  const displayed = [];
  history.forEach(s => {
    const label = STEP_LABELS[s];
    if (!label || seen.has(label)) return;
    seen.add(label);
    displayed.push({ step: s, label });
  });
  if (displayed.length <= 1) return null;
  return (
    <div className="plan-breadcrumb">
      {displayed.map(({ step, label }, i) => {
        const isCurrent = step === current || (step === 'datepick' && current === 'daterange');
        return (
          <span key={step} className="plan-breadcrumb-item">
            {i > 0 && <span className="plan-breadcrumb-sep">›</span>}
            {isCurrent
              ? <span className="plan-breadcrumb-current">{label}</span>
              : <button className="plan-breadcrumb-btn" onClick={() => onJump(step)}>{label}</button>
            }
          </span>
        );
      })}
    </div>
  );
}

// ─── Session flow ─────────────────────────────────────────────────────────────

function SessionFlow({ members, onCreated, onCancel }) {
  const [step, setStep] = useState('theme');         // theme | plantpick | tasktype | subfilter | datepick | daterange
  const [stepHistory, setStepHistory] = useState(['theme']);
  const [theme, setTheme] = useState(null);
  const [taskType, setTaskType] = useState('health_check');
  const [candidates, setCandidates] = useState([]);  // plants from rounds endpoint
  const [subFilterOptions, setSubFilterOptions] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [plantPickQuery, setPlantPickQuery] = useState('');
  const [plantPickSelected, setPlantPickSelected] = useState(new Set()); // Set of plant_id
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [assigneeId, setAssigneeId] = useState(null);
  const [distribution, setDistribution] = useState('even');
  const [preview, setPreview] = useState({});        // { plant_id: date }
  const [recurrence, setRecurrence] = useState({ recurrence_type: 'once', interval_days: 5, total_cycles: 3 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isTreatment = taskType === 'treatment';

  const selectedTheme = PLAN_THEMES.find(t => t.key === theme);

  function pushStep(newStep) {
    setStepHistory(h => [...h, newStep]);
    setStep(newStep);
  }

  function jumpToStep(targetStep) {
    setStepHistory(h => {
      const idx = h.indexOf(targetStep);
      return idx >= 0 ? h.slice(0, idx + 1) : h;
    });
    setStep(targetStep);
  }

  // Step 1 → 2: theme selected
  function handleThemeSelect(key) {
    setTheme(key);
    const opt = PLAN_THEMES.find(t => t.key === key);
    setLoading(true);
    setError(null);
    // Custom theme: fetch all active plants for manual picking
    const url = key === 'custom' ? '/plants/rounds?group_by=none' : `/plants/rounds?group_by=${key}`;
    fetch(url)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        setCandidates(data);
        if (opt?.hasSubFilter) {
          // Derive sub-filter options
          if (key === 'location') {
            const names = [...new Set(data.flatMap(p => p.locations))].sort();
            setSubFilterOptions(names);
          } else if (key === 'growing_method') {
            const names = [...new Set(data.map(p => p.growing_method).filter(Boolean))].sort();
            setSubFilterOptions(names);
          } else if (key === 'tag') {
            const names = [...new Set(data.flatMap(p => p.tags.map(t => t.name)))].sort();
            setSubFilterOptions(names);
          } else if (key === 'status') {
            const names = [...new Set(data.map(p => p.status))];
            setSubFilterOptions(names);
          }
        }
        setLoading(false);
        const nextStep = key === 'custom' ? 'plantpick' : 'tasktype';
        setStepHistory(['theme', nextStep]);
        setStep(nextStep);
      })
      .catch(() => { setError('Failed to load plants.'); setLoading(false); });
  }

  // Filtered candidates after sub-filter selection (or manual pick for custom)
  const filteredCandidates = theme === 'custom'
    ? candidates.filter(p => plantPickSelected.has(p.plant_id))
    : selectedFilters.length === 0 ? candidates : candidates.filter(p => {
        if (theme === 'location')       return p.locations.some(l => selectedFilters.includes(l));
        if (theme === 'growing_method') return selectedFilters.includes(p.growing_method);
        if (theme === 'tag')            return p.tags.some(t => selectedFilters.includes(t.name));
        if (theme === 'status')         return selectedFilters.includes(p.status);
        return true;
      });

  // Plants visible in picker after search filter
  const plantPickVisible = candidates.filter(p => {
    if (!plantPickQuery) return true;
    const q = plantPickQuery.toLowerCase();
    return (p.display_name || '').toLowerCase().includes(q) ||
           (p.specimen_id || '').toLowerCase().includes(q) ||
           (p.locations || []).some(l => l.toLowerCase().includes(q));
  });

  // Build preview: distribute plants across days in the range.
  // Accepts range/dist explicitly to avoid stale-closure bugs when called
  // immediately after a state setter.
  function buildPreview(plants, range = dateRange, dist = distribution) {
    if (!range.start || !range.end) return {};
    const days = [];
    const s = new Date(range.start + 'T00:00:00');
    const e = new Date(range.end + 'T00:00:00');
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      days.push(formatDate(new Date(d)));
    }
    if (days.length === 0) return {};

    const map = {};
    if (dist === 'location' && days.length > 1) {
      const byLocation = {};
      plants.forEach(p => {
        const loc = p.locations[0] || '__none__';
        (byLocation[loc] = byLocation[loc] || []).push(p);
      });
      const groups = Object.values(byLocation);
      groups.forEach((group, gi) => {
        const dayIdx = gi % days.length;
        group.forEach(p => { map[String(p.plant_id)] = days[dayIdx]; });
      });
    } else {
      // Even round-robin (or forced when single day)
      plants.forEach((p, i) => { map[String(p.plant_id)] = days[i % days.length]; });
    }
    return map;
  }

  function handleGoToDateRange() {
    const filtered = filteredCandidates;
    if (filtered.length === 0) { setError('No plants match the selected filter.'); return; }
    pushStep('datepick');
  }

  function handleDateQuickPick(start, end) {
    const newRange = { start, end };
    setDateRange(newRange);
    setPreview(buildPreview(filteredCandidates, newRange));
    pushStep('daterange');
  }

  function handleDateRangeChange(field, val) {
    const newRange = { ...dateRange, [field]: val };
    setDateRange(newRange);
    setPreview(buildPreview(filteredCandidates, newRange));
  }

  function handleDistributionChange(val) {
    setDistribution(val);
    setPreview(buildPreview(filteredCandidates, dateRange, val));
  }

  function handleConfirm() {
    setSaving(true);
    setError(null);
    const rec = isTreatment && recurrence.recurrence_type !== 'once' ? {
      recurrence_type: recurrence.recurrence_type,
      interval_days: recurrence.interval_days,
      total_cycles: recurrence.total_cycles,
    } : null;

    fetch('/api/plan/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        task_type: taskType,
        date_assignments: preview,
        assigned_to_user_id: assigneeId || null,
        recurrence: rec,
      }),
    })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.detail || 'Error')))
      .then(data => { setSaving(false); onCreated(data); })
      .catch(err => { setSaving(false); setError(String(err)); });
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (step === 'theme') {
    return (
      <div className="plan-session-flow">
        <header className="sub-header">
          <button className="sub-header-back" onClick={onCancel} aria-label="Cancel" title="Cancel">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>Plan a Session</h1>
            <p className="sub-header-subtitle">Choose a theme</p>
          </div>
        </header>
        <div className="plan-theme-list">
          {PLAN_THEMES.map(t => (
            <button key={t.key} className="plan-theme-option" onClick={() => handleThemeSelect(t.key)}>
              <Icon name={t.icon} size={20} />
              <div>
                <div className="plan-theme-option-label">{t.label}</div>
                <div className="plan-theme-option-desc">{t.description}</div>
              </div>
              <Icon name="chevronRight" size={16} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="plan-session-flow"><p className="health-loading">Loading plants…</p></div>;
  }

  if (step === 'plantpick') {
    return (
      <div className="plan-session-flow">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => jumpToStep('theme')} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>Pick plants</h1>
            <SessionBreadcrumb history={stepHistory} current={step} onJump={jumpToStep} />
          </div>
        </header>
        <div className="plan-plantpick-search">
          <input
            className="form-input"
            type="search"
            placeholder="Search by name, ID, or location…"
            value={plantPickQuery}
            onChange={e => setPlantPickQuery(e.target.value)}
            autoFocus
          />
        </div>
        <div className="plan-plantpick-list">
          {plantPickVisible.map(p => {
            const selected = plantPickSelected.has(p.plant_id);
            return (
              <div
                key={p.plant_id}
                className={`plan-plantpick-item ${selected ? 'plan-plantpick-item--selected' : ''}`}
                onClick={() => setPlantPickSelected(prev => {
                  const next = new Set(prev);
                  next.has(p.plant_id) ? next.delete(p.plant_id) : next.add(p.plant_id);
                  return next;
                })}
              >
                <div className="plan-plantpick-circle">
                  {selected && <Icon name="check" size={13} />}
                </div>
                {p.cover_photo_id ? (
                  <img src={photoUrl(p.cover_photo_id, p.cover_photo_rotation_version, p.cover_photo_created_at)} alt="" className="plan-plantpick-thumb" style={{ objectPosition: `${p.cover_photo_offset_x ?? 50}% ${p.cover_photo_offset_y ?? 50}%`, transform: `scale(${p.cover_photo_zoom ?? 1})`, transformOrigin: `${p.cover_photo_offset_x ?? 50}% ${p.cover_photo_offset_y ?? 50}%` }} />
                ) : (
                  <div className="plan-plantpick-thumb plan-plantpick-thumb--empty"><Icon name="leaf" size={16} /></div>
                )}
                <div className="plan-plantpick-info">
                  <span className="plan-plantpick-name">{p.display_name}</span>
                  <span className="plan-plantpick-sub">
                    {p.specimen_id}
                    {p.locations && p.locations.length > 0 && ` · ${p.locations[0]}`}
                    {p.days_since != null && ` · ${p.days_since}d ago`}
                  </span>
                </div>
              </div>
            );
          })}
          {plantPickVisible.length === 0 && (
            <p className="plan-plantpick-hint">No plants match your search.</p>
          )}
        </div>
        <div className="plan-session-footer">
          {plantPickSelected.size === 0 ? (
            <p className="plan-plantpick-hint">Tap plants above to select them</p>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => pushStep('tasktype')}
            >
              Next · {plantPickSelected.size} plant{plantPickSelected.size !== 1 ? 's' : ''} selected
            </button>
          )}
        </div>
      </div>
    );
  }

  if (step === 'tasktype') {
    return (
      <div className="plan-session-flow">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => jumpToStep(theme === 'custom' ? 'plantpick' : 'theme')} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>What type of care?</h1>
            <SessionBreadcrumb history={stepHistory} current={step} onJump={jumpToStep} />
          </div>
        </header>
        <div className="plan-type-list">
          {TASK_TYPES.map(t => (
            <div key={t.value}>
              <button
                className={`plan-type-option ${taskType === t.value ? 'plan-type-option--selected' : ''}`}
                onClick={() => setTaskType(t.value)}
              >
                <Icon name={t.icon} size={18} />
                <span>{t.label}</span>
                <span className="plan-type-check"><Icon name="check" size={16} /></span>
              </button>
              {t.value === 'treatment' && taskType === 'treatment' && (
                <div className="plan-treatment-inline">
                  <div className="plan-treatment-row">
                    <label>Every</label>
                    <input type="number" min="1" max="30" className="form-input plan-cycle-input"
                      value={recurrence.interval_days}
                      onChange={e => setRecurrence(r => ({ ...r, interval_days: parseInt(e.target.value) || 5, recurrence_type: 'cycles' }))} />
                    <label>days ×</label>
                    <input type="number" min="1" max="10" className="form-input plan-cycle-input"
                      value={recurrence.total_cycles}
                      onChange={e => setRecurrence(r => ({ ...r, total_cycles: parseInt(e.target.value) || 3, recurrence_type: 'cycles' }))} />
                    <label>cycles</label>
                  </div>
                  <p className="plan-treatment-note">Each plant gets {recurrence.total_cycles} scheduled treatment tasks.</p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="plan-session-footer">
          <button
            className="btn btn-primary"
            onClick={() => selectedTheme?.hasSubFilter ? pushStep('subfilter') : handleGoToDateRange()}
          >
            Next
          </button>
        </div>
      </div>
    );
  }

  if (step === 'subfilter') {
    return (
      <div className="plan-session-flow">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => jumpToStep('tasktype')} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>Filter plants</h1>
            <SessionBreadcrumb history={stepHistory} current={step} onJump={jumpToStep} />
          </div>
        </header>
        <div className="plan-subfilter-list">
          {subFilterOptions.map(opt => {
            const checked = selectedFilters.includes(opt);
            return (
              <label key={opt} className="plan-subfilter-option">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => setSelectedFilters(prev =>
                    checked ? prev.filter(x => x !== opt) : [...prev, opt]
                  )}
                />
                <span>{opt}</span>
                <span className="plan-subfilter-count">
                  {candidates.filter(p => {
                    if (theme === 'location')       return p.locations.includes(opt);
                    if (theme === 'growing_method') return p.growing_method === opt;
                    if (theme === 'tag')            return p.tags.some(t => t.name === opt);
                    if (theme === 'status')         return p.status === opt;
                    return false;
                  }).length} plants
                </span>
              </label>
            );
          })}
        </div>
        <div className="plan-session-footer">
          <button className="btn btn-primary" onClick={handleGoToDateRange}>
            Next · {filteredCandidates.length} plants
          </button>
        </div>
        {error && <p className="error" style={{ padding: '0 1rem' }}>{error}</p>}
      </div>
    );
  }

  if (step === 'datepick') {
    const backStep = selectedTheme?.hasSubFilter ? 'subfilter' : 'tasktype';
    const quickOpts = getDateQuickOptions();
    return (
      <div className="plan-session-flow">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => jumpToStep(backStep)} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>When?</h1>
            <SessionBreadcrumb history={stepHistory} current={step} onJump={jumpToStep} />
          </div>
        </header>
        <div className="plan-datepick-list">
          {quickOpts.map(opt => (
            <button key={opt.label} className="plan-datepick-option" onClick={() => handleDateQuickPick(opt.start, opt.end)}>
              <span className="plan-datepick-label">{opt.label}</span>
              <span className="plan-datepick-dates">
                {opt.start === opt.end ? formatDisplayDate(opt.start) : `${formatDisplayDate(opt.start)} – ${formatDisplayDate(opt.end)}`}
              </span>
              <Icon name="chevronRight" size={16} />
            </button>
          ))}
          <button className="plan-datepick-option plan-datepick-option--custom" onClick={() => { setDateRange({ start: '', end: '' }); pushStep('daterange'); }}>
            <span className="plan-datepick-label">Custom range…</span>
            <span className="plan-datepick-dates">Pick any dates</span>
            <Icon name="chevronRight" size={16} />
          </button>
        </div>
      </div>
    );
  }

  if (step === 'daterange') {
    const days = [];
    if (dateRange.start && dateRange.end) {
      const s = new Date(dateRange.start + 'T00:00:00');
      const e = new Date(dateRange.end + 'T00:00:00');
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        days.push(formatDate(new Date(d)));
      }
    }
    const previewEntries = Object.entries(preview);

    return (
      <div className="plan-session-flow">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => jumpToStep('datepick')} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>Pick dates</h1>
            <SessionBreadcrumb history={stepHistory} current={step} onJump={jumpToStep} />
          </div>
        </header>

        <div className="plan-daterange-form">
          <div className="plan-daterange-row">
            <div className="form-field">
              <label className="form-label">Start date</label>
              <input type="date" className="form-input" value={dateRange.start}
                onChange={e => handleDateRangeChange('start', e.target.value)} />
            </div>
            <div className="form-field">
              <label className="form-label">End date</label>
              <input type="date" className="form-input" value={dateRange.end}
                onChange={e => handleDateRangeChange('end', e.target.value)} />
            </div>
          </div>

          <div className="form-field">
            <label className="form-label">Assign to</label>
            <select className="form-input" value={assigneeId || ''} onChange={e => setAssigneeId(e.target.value || null)}>
              <option value="">No one assigned</option>
              {members.map(m => (
                <option key={m.id} value={m.id}>{m.display_name || m.email}</option>
              ))}
            </select>
          </div>

          {days.length > 1 && (
            <div className="form-field">
              <label className="form-label">Spread plants across days</label>
              <div className="plan-dist-segs">
                <button
                  className={`plan-dist-seg ${distribution === 'even' ? 'plan-dist-seg--active' : ''}`}
                  onClick={() => handleDistributionChange('even')}
                >
                  Even spread
                </button>
                <button
                  className={`plan-dist-seg ${distribution === 'location' ? 'plan-dist-seg--active' : ''}`}
                  onClick={() => handleDistributionChange('location')}
                >
                  By location
                </button>
              </div>
            </div>
          )}

          {previewEntries.length > 0 && (
            <div className="plan-preview-summary">
              <p className="plan-preview-title">Preview — {previewEntries.length} tasks across {days.length} day{days.length !== 1 ? 's' : ''}</p>
              {days.map(day => {
                const dayPlants = previewEntries.filter(([, d]) => d === day);
                return (
                  <div key={day} className="plan-preview-day">
                    <span className="plan-preview-day-label">{formatDisplayDate(day)}</span>
                    <span className="plan-preview-day-count">{dayPlants.length} plants</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {error && <p className="error" style={{ padding: '0 1rem' }}>{error}</p>}
        <div className="plan-session-footer">
          <button className="btn btn-primary" onClick={handleConfirm} disabled={saving || previewEntries.length === 0}>
            {saving ? 'Scheduling…' : `Schedule ${previewEntries.length} tasks`}
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// ─── Add single task modal ────────────────────────────────────────────────────

function AddTaskModal({ members, onSaved, onCancel }) {
  const today = formatDate(new Date());
  const [plantQuery, setPlantQuery] = useState('');
  const [plantResults, setPlantResults] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [form, setForm] = useState({
    task_type: 'health_check',
    scheduled_date: today,
    assigned_to_user_id: null,
    notes: '',
    recurrence_type: 'once',
    interval_days: 7,
    total_cycles: 3,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (plantQuery.length < 2) { setPlantResults([]); return; }
    const timeout = setTimeout(() => {
      fetch(`/plants?q=${encodeURIComponent(plantQuery)}`)
        .then(r => r.ok ? r.json() : [])
        .then(data => setPlantResults((data.plants || data).slice(0, 8)))
        .catch(() => setPlantResults([]));
    }, 250);
    return () => clearTimeout(timeout);
  }, [plantQuery]);

  function handleSave() {
    if (!selectedPlant) { setError('Select a plant first.'); return; }
    setSaving(true);
    setError(null);

    const body = {
      plant_id: selectedPlant.id,
      task_type: form.task_type,
      starts_on: form.scheduled_date,
      assigned_to_user_id: form.assigned_to_user_id ? parseInt(form.assigned_to_user_id) : null,
      notes: form.notes || null,
      recurrence_type: form.recurrence_type,
      interval_days: form.recurrence_type !== 'once' ? form.interval_days : null,
      total_cycles: form.recurrence_type === 'cycles' ? form.total_cycles : null,
    };

    fetch('/api/plan/schedules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.detail || 'Error')))
      .then(data => { setSaving(false); onSaved(data); })
      .catch(err => { setSaving(false); setError(String(err)); });
  }

  return createPortal(
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-card plan-add-task-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Task</h2>
          <button type="button" className="btn-icon" onClick={onCancel} aria-label="Close" title="Close"><Icon name="x" size={18} /></button>
        </div>

        {/* Plant search */}
        <div className="form-field">
          <label className="form-label">Plant</label>
          {selectedPlant ? (
            <div className="plan-selected-plant">
              <span>{plantDisplayName(selectedPlant)}</span>
              <span className="plan-task-specimen"> · {selectedPlant.specimen_id}</span>
              <button className="plan-clear-plant" onClick={() => { setSelectedPlant(null); setPlantQuery(''); }}>
                <Icon name="x" size={14} />
              </button>
            </div>
          ) : (
            <div className="plan-plant-search">
              <input
                type="text"
                className="form-input"
                placeholder="Search by name or specimen ID…"
                value={plantQuery}
                onChange={e => setPlantQuery(e.target.value)}
                autoFocus
              />
              {plantResults.length > 0 && (
                <div className="plan-plant-results">
                  {plantResults.map(p => (
                    <button
                      key={p.id}
                      className="plan-plant-result"
                      onClick={() => { setSelectedPlant(p); setPlantQuery(''); setPlantResults([]); }}
                    >
                      <span>{plantDisplayName(p)}</span>
                      <span className="plan-task-specimen"> · {p.specimen_id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Task type */}
        <div className="form-field">
          <label className="form-label">Task type</label>
          <select className="form-input" value={form.task_type}
            onChange={e => setForm(f => ({ ...f, task_type: e.target.value }))}>
            {TASK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {/* Date */}
        <div className="form-field">
          <label className="form-label">Date</label>
          <input type="date" className="form-input" value={form.scheduled_date}
            onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} />
        </div>

        {/* Assignee */}
        {members.length > 1 && (
          <div className="form-field">
            <label className="form-label">Assign to</label>
            <select className="form-input" value={form.assigned_to_user_id || ''}
              onChange={e => setForm(f => ({ ...f, assigned_to_user_id: e.target.value || null }))}>
              <option value="">No one assigned</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.display_name || m.email}</option>)}
            </select>
          </div>
        )}

        {/* Notes */}
        <div className="form-field">
          <label className="form-label">Notes</label>
          <textarea className="form-input" rows={2} value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </div>

        {/* Recurrence */}
        <div className="form-field">
          <label className="form-label">Repeat</label>
          <select className="form-input" value={form.recurrence_type}
            onChange={e => setForm(f => ({ ...f, recurrence_type: e.target.value }))}>
            <option value="once">Once</option>
            <option value="interval">Every N days (ongoing)</option>
            <option value="cycles">Every N days × M cycles</option>
          </select>
        </div>
        {form.recurrence_type !== 'once' && (
          <div className="plan-recurrence-row">
            <div className="form-field">
              <label className="form-label">Every (days)</label>
              <input type="number" min="1" max="90" className="form-input"
                value={form.interval_days}
                onChange={e => setForm(f => ({ ...f, interval_days: parseInt(e.target.value) || 7 }))} />
            </div>
            {form.recurrence_type === 'cycles' && (
              <div className="form-field">
                <label className="form-label">Cycles</label>
                <input type="number" min="1" max="10" className="form-input"
                  value={form.total_cycles}
                  onChange={e => setForm(f => ({ ...f, total_cycles: parseInt(e.target.value) || 3 }))} />
              </div>
            )}
          </div>
        )}

        {error && <p className="error">{error}</p>}
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !selectedPlant}>
            {saving ? 'Saving…' : 'Add Task'}
          </button>
          <button className="btn btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Pull-to-refresh hook ─────────────────────────────────────────────────────

// ─── Filter bars ──────────────────────────────────────────────────────────────

// Generic multiselect chip bar.
// `selected` is a Set of active ids; empty Set = "All" (no filter).
// Clicking a chip toggles it; clicking "All" clears the set.
function FilterChipBar({ chips, selected, onToggle, onClearAll, label }) {
  const allActive = selected.size === 0;
  return (
    <div className="plan-filter-bar" aria-label={label}>
      {label && <span className="plan-filter-label">{label}</span>}
      <button
        className={`plan-filter-chip${allActive ? ' plan-filter-chip--active' : ''}`}
        onClick={onClearAll}
      >
        All
      </button>
      {chips.map(chip => {
        const active = selected.has(chip.id);
        return (
          <button
            key={String(chip.id)}
            className={`plan-filter-chip${active ? ' plan-filter-chip--active' : ''}`}
            onClick={() => onToggle(chip.id)}
            title={chip.title || chip.label}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

function AssigneeFilterBar({ members, tasks, selected, onToggle, onClearAll }) {
  const assignedIds = new Set(tasks.map(t => t.assigned_to_user_id).filter(Boolean));
  const hasUnassigned = tasks.some(t => !t.assigned_to_user_id);
  const activeMembers = members.filter(m => assignedIds.has(m.id));

  // Only show if there are at least 2 distinct assignee options (members + possibly unassigned)
  const totalOptions = activeMembers.length + (hasUnassigned ? 1 : 0);
  if (totalOptions < 2) return null;

  const chips = [
    ...activeMembers.map(m => {
      const full = m.display_name || m.email || '?';
      return { id: m.id, label: full.split(' ')[0], title: full };
    }),
    ...(hasUnassigned ? [{ id: 'unassigned', label: 'Unassigned' }] : []),
  ];

  return (
    <FilterChipBar
      chips={chips}
      selected={selected}
      onToggle={onToggle}
      onClearAll={onClearAll}
      label="Assignee"
    />
  );
}

function TaskTypeFilterBar({ tasks, selected, onToggle, onClearAll }) {
  const presentTypes = new Set(tasks.map(t => t.task_type));
  const chips = TASK_TYPES
    .filter(tt => presentTypes.has(tt.value))
    .map(tt => ({ id: tt.value, label: tt.label }));

  if (chips.length < 2) return null;

  return (
    <FilterChipBar
      chips={chips}
      selected={selected}
      onToggle={onToggle}
      onClearAll={onClearAll}
      label="Type"
    />
  );
}

// ─── Week strip ───────────────────────────────────────────────────────────────

const STRIP_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function WeekStrip({ weekDays, tasks, todayISO, weekLabel, onPrevWeek, onNextWeek, onGoToday, onDayClick }) {
  const isCurrentWeek = weekDays.includes(todayISO);

  return (
    <div className="plan-week-strip-wrap">
      <div className="plan-strip-nav-row">
        <button className="btn-icon plan-strip-nav-btn" onClick={onPrevWeek} aria-label="Previous week" title="Previous week">
          <Icon name="chevronLeft" size={18} />
        </button>
        <span className="plan-strip-week-label">{weekLabel}</span>
        <button className="btn-icon plan-strip-nav-btn" onClick={onNextWeek} aria-label="Next week" title="Next week">
          <Icon name="chevronRight" size={18} />
        </button>
        {!isCurrentWeek && (
          <button className="plan-strip-today-btn" onClick={onGoToday}>Today</button>
        )}
      </div>
      <div className="plan-week-strip">
        {weekDays.map((date, i) => {
          const dayTasks = tasks.filter(t => t.scheduled_date === date && t.status !== 'overdue');
          const isToday = date === todayISO;
          const hasPending = dayTasks.some(t => t.status === 'pending');
          const allDone = dayTasks.length > 0 && dayTasks.every(t => t.status === 'done' || t.status === 'skipped');
          const dotState = dayTasks.length === 0 ? null : allDone ? 'done' : hasPending ? 'pending' : null;
          return (
            <button
              key={date}
              className={`plan-strip-day${isToday ? ' plan-strip-day--today' : ''}`}
              onClick={() => onDayClick(date)}
              aria-label={`${STRIP_LETTERS[i]} ${parseInt(date.split('-')[2], 10)}`}
            >
              <span className="plan-strip-letter">{STRIP_LETTERS[i]}</span>
              <span className="plan-strip-num">{parseInt(date.split('-')[2], 10)}</span>
              {dotState && <span className={`plan-strip-dot plan-strip-dot--${dotState}`} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Plan component ──────────────────────────────────────────────────────

export default function Plan() {
  const navigate = useNavigate();
  const { hasSeen } = useGuidance();
  const [showPlanIntro, setShowPlanIntro] = useState(() => false); // set after plant count known
  const [showPlanHelp, setShowPlanHelp] = useState(false);
  const [plantCount, setPlantCount] = useState(null); // null = loading

  // Week navigation
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));

  // Data
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Expanded days (accordion)
  const [expandedDays, setExpandedDays] = useState(() => new Set([formatDate(new Date())]));

  // Nav overflow menu
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const navMenuRef = useRef(null);

  // Close nav menu when clicking outside
  useEffect(() => {
    if (!navMenuOpen) return;
    function handleClickOutside(e) {
      if (navMenuRef.current && !navMenuRef.current.contains(e.target)) {
        setNavMenuOpen(false);
        setConfirmClearAll(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [navMenuOpen]);

  // Selection mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Current "view" / overlay
  const [view, setView] = useState('week'); // 'week' | 'session' | 'add-task' | 'log-task'

  // Confirm clear all state
  const [confirmClearAll, setConfirmClearAll] = useState(false);

  // Log task state
  const [logTask, setLogTask] = useState(null);
  const [logSaving, setLogSaving] = useState(false);

  // Multiselect filters — empty Set means "show all"
  const [filterUserIds, setFilterUserIds]     = useState(new Set());
  const [filterTaskTypes, setFilterTaskTypes] = useState(new Set());

  function toggleFilterUser(id) {
    setFilterUserIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleFilterTaskType(type) {
    setFilterTaskTypes(prev => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  }

  // Derived week days
  const weekDays = Array.from({ length: 7 }, (_, i) => formatDate(addDays(weekStart, i)));

  const startISO = weekDays[0];
  const endISO   = weekDays[6];

  const weekLabel = (() => {
    const s = new Date(startISO + 'T00:00:00');
    const e = new Date(endISO + 'T00:00:00');
    const sLabel = `${MONTH_NAMES[s.getMonth()]} ${s.getDate()}`;
    const eLabel = s.getMonth() === e.getMonth()
      ? `${e.getDate()}`
      : `${MONTH_NAMES[e.getMonth()]} ${e.getDate()}`;
    return `${sLabel} – ${eLabel}, ${s.getFullYear()}`;
  })();

  // Derived filtered task list (for display only; batch selection still uses full `tasks`)
  const visibleTasks = useMemo(() => tasks.filter(t => {
    if (filterUserIds.size > 0) {
      const uid = t.assigned_to_user_id ?? 'unassigned';
      if (!filterUserIds.has(uid)) return false;
    }
    if (filterTaskTypes.size > 0) {
      if (!filterTaskTypes.has(t.task_type)) return false;
    }
    return true;
  }), [filterUserIds, filterTaskTypes, tasks]);

  // When filters are active, automatically expand days that contain matching tasks.
  // When all filters are cleared, leave the expanded state as-is (don't collapse).
  useEffect(() => {
    const anyFilterActive = filterUserIds.size > 0 || filterTaskTypes.size > 0;
    if (!anyFilterActive) return;
    setExpandedDays(prev => {
      const next = new Set(prev);
      visibleTasks.forEach(t => {
        if (t.status !== 'overdue') next.add(t.scheduled_date);
        else next.add('__overdue__');
      });
      return next;
    });
  }, [visibleTasks, filterUserIds, filterTaskTypes]);

  // Fetch tasks + members
  const fetchTasks = useCallback(() => {
    setLoading(true);
    setError(null);
    return fetch(`/api/plan?start=${startISO}&end=${endISO}`)
      .then(r => r.ok ? r.json() : Promise.reject('Failed to load plan'))
      .then(data => { setTasks(data); setLoading(false); })
      .catch(err => { setError(String(err)); setLoading(false); });
  }, [startISO, endISO]);

  const refreshing = usePullToRefresh(fetchTasks);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  useEffect(() => {
    fetch('/api/plan/household-members')
      .then(r => r.ok ? r.json() : [])
      .then(setMembers)
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/plants')
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const count = data.length;
        setPlantCount(count);
        if (!hasSeen('seen_plan_intro') && count > 0) {
          setShowPlanIntro(true);
        }
      })
      .catch(() => setPlantCount(0));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand today and days with overdue tasks
  useEffect(() => {
    const todayISO = formatDate(new Date());
    const initial = new Set();
    if (weekDays.includes(todayISO)) initial.add(todayISO);
    tasks.filter(t => t.status === 'overdue').forEach(() => initial.add('__overdue__'));
    setExpandedDays(prev => {
      // Only add; don't collapse days the user already expanded
      const merged = new Set(prev);
      initial.forEach(d => merged.add(d));
      return merged;
    });
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Week navigation ─────────────────────────────────────────────────────────

  function prevWeek() { setWeekStart(d => addDays(d, -7)); }
  function nextWeek() { setWeekStart(d => addDays(d, 7)); }
  function goToday()  { setWeekStart(getMonday(new Date())); }

  function scrollToDay(date) {
    setExpandedDays(prev => { const next = new Set(prev); next.add(date); return next; });
    setTimeout(() => {
      const el = document.querySelector(`[data-date="${date}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  // ── Selection ───────────────────────────────────────────────────────────────

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelectedIds(new Set());
  }

  // ── Task actions (individual) ───────────────────────────────────────────────

  function handleSkip(task) {
    fetch(`/api/plan/tasks/${task.id}/skip`, { method: 'PATCH' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(updated => setTasks(prev => prev.map(t => t.id === updated.id ? updated : t)))
      .catch(() => setError('Failed to skip task'));
  }

  function handleUnmark(task) {
    fetch(`/api/plan/tasks/${task.id}/unmark`, { method: 'PATCH' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(updated => setTasks(prev => prev.map(t => t.id === updated.id ? updated : t)))
      .catch(() => setError('Failed to unmark task'));
  }

  function handleLog(task) {
    setLogTask(task);
    setView('log-task');
  }

  function handleLogSave(payload) {
    setLogSaving(true);
    fetch(`/api/plan/tasks/${logTask.id}/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(updated => {
        setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
        setLogSaving(false);
        setLogTask(null);
        setView('week');
      })
      .catch(() => { setLogSaving(false); setError('Failed to log task'); });
  }

  function handleDelete(task) {
    fetch(`/api/plan/tasks/${task.id}`, { method: 'DELETE' })
      .then(r => { if (r.ok) setTasks(prev => prev.filter(t => t.id !== task.id)); })
      .catch(() => setError('Failed to delete task'));
  }

  function handleCompleteAll(pendingTaskList) {
    const taskIds = pendingTaskList
      .filter(t => t.status !== 'done' && t.status !== 'skipped')
      .map(t => t.id);
    if (taskIds.length === 0) return;
    fetch('/api/plan/tasks/batch-complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_ids: taskIds, date: formatDate(new Date()) }),
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(() => { exitSelectMode(); setTasks(prev => prev.map(t => taskIds.includes(t.id) ? { ...t, status: 'done' } : t)); fetchTasks(); })
      .catch(() => setError('Failed to complete tasks'));
  }

  // ── Reschedule tasks ────────────────────────────────────────────────────────

  function handleReschedule(taskId, newDate) {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, scheduled_date: newDate, status: 'pending' } : t));
    fetch(`/api/plan/tasks/${taskId}/reschedule`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ new_date: newDate }),
    })
      .then(r => { if (!r.ok) throw new Error(r.status); fetchTasks(); })
      .catch(() => setError('Failed to reschedule task'));
  }

  function handleReassignDay(date, assignedToUserId) {
    setTasks(prev => prev.map(t =>
      t.scheduled_date === date && (t.status === 'pending' || t.status === 'overdue')
        ? { ...t, assigned_to_user_id: assignedToUserId } : t
    ));
    fetch('/api/plan/tasks/reassign-day', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, assigned_to_user_id: assignedToUserId }),
    })
      .then(r => { if (!r.ok) throw new Error(r.status); fetchTasks(); })
      .catch(() => setError('Failed to reassign tasks'));
  }

  function handleChangeTypeDay(date, taskType) {
    setTasks(prev => prev.map(t =>
      t.scheduled_date === date && (t.status === 'pending' || t.status === 'overdue')
        ? { ...t, task_type: taskType } : t
    ));
    fetch('/api/plan/tasks/change-type-day', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, task_type: taskType }),
    })
      .then(r => { if (!r.ok) throw new Error(r.status); fetchTasks(); })
      .catch(() => setError('Failed to change task types'));
  }

  function handleReassign(taskId, assignedToUserId) {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, assigned_to_user_id: assignedToUserId } : t));
    fetch(`/api/plan/tasks/${taskId}/reassign`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assigned_to_user_id: assignedToUserId }),
    })
      .then(r => { if (!r.ok) throw new Error(r.status); fetchTasks(); })
      .catch(() => setError('Failed to reassign task'));
  }

  function handleChangeTaskType(taskId, taskType) {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, task_type: taskType } : t));
    fetch(`/api/plan/tasks/${taskId}/task-type`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_type: taskType }),
    })
      .then(r => { if (!r.ok) throw new Error(r.status); fetchTasks(); })
      .catch(() => setError('Failed to change task type'));
  }

  function handleRescheduleDay(fromDate, toDate) {
    setTasks(prev => prev.map(t =>
      t.scheduled_date === fromDate && (t.status === 'pending' || t.status === 'overdue')
        ? { ...t, scheduled_date: toDate, status: 'pending' } : t
    ));
    fetch('/api/plan/tasks/reschedule-day', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ from_date: fromDate, to_date: toDate }),
    })
      .then(r => { if (!r.ok) throw new Error(r.status); fetchTasks(); })
      .catch(() => setError('Failed to reschedule day'));
  }

  function handleRescheduleOverdue(toDate) {
    setTasks(prev => prev.map(t =>
      t.status === 'overdue' ? { ...t, scheduled_date: toDate, status: 'pending' } : t
    ));
    fetch('/api/plan/tasks/reschedule-overdue', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to_date: toDate }),
    })
      .then(r => { if (!r.ok) throw new Error(r.status); fetchTasks(); })
      .catch(() => setError('Failed to reschedule overdue tasks'));
  }

  // ── Clear tasks ─────────────────────────────────────────────────────────────

  function handleClearDay(date) {
    fetch(`/api/plan/tasks/pending?date=${date}`, { method: 'DELETE' })
      .then(r => { if (!r.ok) throw new Error(r.status); setTasks(prev => prev.filter(t => t.scheduled_date !== date || t.status === 'done' || t.status === 'skipped')); fetchTasks(); })
      .catch(() => setError('Failed to clear day'));
  }

  function handleClearAllPending() {
    fetch('/api/plan/tasks/pending', { method: 'DELETE' })
      .then(r => { if (!r.ok) throw new Error(r.status); setTasks(prev => prev.filter(t => t.status === 'done' || t.status === 'skipped')); fetchTasks(); })
      .catch(() => setError('Failed to clear pending tasks'));
  }

  // ── Render helpers ──────────────────────────────────────────────────────────

  const tasksByDay = {};
  weekDays.forEach(d => { tasksByDay[d] = []; });
  const overdueTasks = [];

  visibleTasks.forEach(t => {
    if (t.status === 'overdue') {
      overdueTasks.push(t);
    } else if (tasksByDay[t.scheduled_date] !== undefined) {
      tasksByDay[t.scheduled_date].push(t);
    }
  });

  const pendingSelectedTasks = tasks.filter(t => selectedIds.has(t.id) && t.status !== 'done' && t.status !== 'skipped');

  const todayISO = formatDate(new Date());

  // ── Views ───────────────────────────────────────────────────────────────────

  if (view === 'session') {
    return (
      <SessionFlow
        members={members}
        onCreated={() => { setView('week'); fetchTasks(); }}
        onCancel={() => setView('week')}
      />
    );
  }

  if (view === 'add-task') {
    return (
      <AddTaskModal
        members={members}
        onSaved={() => { setView('week'); fetchTasks(); }}
        onCancel={() => setView('week')}
      />
    );
  }

  // Week view
  return (
    <div className="app plan-page">
      {/* Pull-to-refresh indicator */}
      {refreshing && (
        <div className="plan-ptr-indicator">
          <Icon name="rotateCw" size={16} /> Refreshing…
        </div>
      )}

      {/* Page header */}
      <header className="app-header">
        <div className="app-header-brand">
          <img src="/logo_only.svg" alt="Aroidex" className="app-header-logo" />
          <div><h1>Care Plan</h1></div>
        </div>
        <div className="app-header-actions">
          {!selectMode && tasks.some(t => t.status === 'pending' || t.status === 'overdue') && (
            <button className="btn-header-ghost" onClick={() => setSelectMode(true)}>Select</button>
          )}
          {selectMode && (
            <button className="btn-header-ghost" onClick={exitSelectMode}>Cancel</button>
          )}
          <button
            type="button"
            className="back-btn plan-help-btn"
            onClick={() => setShowPlanHelp(true)}
            aria-label="How Care Plan works"
            title="How Care Plan works"
          >
            <Icon name="helpCircle" size={18} />
          </button>
          <div className="plan-nav-more-wrap" ref={navMenuRef}>
            <button
              className="back-btn plan-nav-more-btn"
              onClick={() => setNavMenuOpen(o => !o)}
              aria-label="Plan actions"
              title="Plan actions"
            >
              <Icon name="plus" size={18} />
            </button>
            {navMenuOpen && (
              <div className="plan-nav-menu">
                <button className="plan-nav-menu-item" onClick={() => { setNavMenuOpen(false); setView('session'); }}>
                  <Icon name="clipboardCheck" size={15} /> Plan a Session
                </button>
                <button className="plan-nav-menu-item" onClick={() => { setNavMenuOpen(false); setView('add-task'); }}>
                  <Icon name="plus" size={15} /> Add Single Task
                </button>
                {tasks.some(t => t.status === 'pending' || t.status === 'overdue') && (
                  <>
                    <div className="plan-nav-menu-divider" />
                    <button className="plan-nav-menu-item plan-nav-menu-item--danger"
                      onClick={() => { setNavMenuOpen(false); setConfirmClearAll(true); }}>
                      <Icon name="trash" size={15} /> Clear all pending
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* First-use intro sheet */}
      <GuidanceSheet
        variant="sheet"
        isOpen={showPlanIntro}
        onDismiss={() => setShowPlanIntro(false)}
        title={PLAN_GUIDANCE_TITLE}
        dismissKey="seen_plan_intro"
        primaryAction={{ label: 'Got it', onClick: () => setShowPlanIntro(false) }}
      >
        <PlanGuidanceBody />
      </GuidanceSheet>

      {/* Re-access help sheet */}
      <GuidanceSheet
        variant="sheet"
        isOpen={showPlanHelp}
        onDismiss={() => setShowPlanHelp(false)}
        title={PLAN_GUIDANCE_TITLE}
        primaryAction={{ label: 'Got it', onClick: () => setShowPlanHelp(false) }}
      >
        <PlanGuidanceBody />
      </GuidanceSheet>

      {/* No-plants empty state — permanent, not dismissible */}
      {plantCount === 0 && (
        <div className="plan-no-plants-state">
          <Icon name="calendar" size={48} className="plan-no-plants-icon" />
          <p className="plan-no-plants-title">Care Plan schedules tasks for plants in your collection.</p>
          <div className="plan-no-plants-features">
            <div className="guidance-feature-row">
              <span className="guidance-feature-icon"><Icon name="check" size={16} /></span>
              <span className="guidance-feature-text">Schedule care in advance, by day and plant</span>
            </div>
            <div className="guidance-feature-row">
              <span className="guidance-feature-icon"><Icon name="check" size={16} /></span>
              <span className="guidance-feature-text">Plan sessions and assign tasks in bulk</span>
            </div>
            <div className="guidance-feature-row">
              <span className="guidance-feature-icon"><Icon name="check" size={16} /></span>
              <span className="guidance-feature-text">Overdue tasks surface automatically</span>
            </div>
          </div>
          <p className="plan-no-plants-cta-label">Add a plant to get started.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/new')}
          >
            Add your first plant →
          </button>
        </div>
      )}

      {/* plantCount !== 0 is intentional: null (loading) also passes through so the
          week view renders while the plant-count fetch is in flight. Only 0 hides it. */}
      {plantCount !== 0 && (error ? (
        <ErrorScreen message={error} onRetry={fetchTasks} />
      ) : (
        <>
      {/* Week strip */}
      <WeekStrip
        weekDays={weekDays}
        tasks={tasks}
        todayISO={todayISO}
        weekLabel={weekLabel}
        onPrevWeek={prevWeek}
        onNextWeek={nextWeek}
        onGoToday={goToday}
        onDayClick={scrollToDay}
      />

      {/* Assignee + task-type filter bars */}
      <div className="plan-filter-section">
        <AssigneeFilterBar
          members={members}
          tasks={tasks}
          selected={filterUserIds}
          onToggle={toggleFilterUser}
          onClearAll={() => setFilterUserIds(new Set())}
        />
        <TaskTypeFilterBar
          tasks={tasks}
          selected={filterTaskTypes}
          onToggle={toggleFilterTaskType}
          onClearAll={() => setFilterTaskTypes(new Set())}
        />
      </div>

      {confirmClearAll && (
        <div className="plan-confirm-bar">
          <span>Clear all {tasks.filter(t => t.status === 'pending' || t.status === 'overdue').length} pending tasks?</span>
          <div className="plan-confirm-bar-actions">
            <button className="btn btn-sm plan-confirm-bar-yes"
              onClick={() => { setConfirmClearAll(false); handleClearAllPending(); }}>
              Yes, clear all
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => setConfirmClearAll(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading && <p className="health-loading">Loading…</p>}

      {/* Overdue section */}
      {overdueTasks.length > 0 && (
        <OverdueSection
          tasks={overdueTasks}
          isOpen={expandedDays.has('__overdue__')}
          onToggle={() => setExpandedDays(prev => {
            const next = new Set(prev);
            next.has('__overdue__') ? next.delete('__overdue__') : next.add('__overdue__');
            return next;
          })}
          selectMode={selectMode}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onSkip={handleSkip}
          onLog={handleLog}
          onDelete={handleDelete}
          onCompleteAll={handleCompleteAll}
          onReschedule={handleReschedule}
          onRescheduleOverdue={handleRescheduleOverdue}
          onClearOverdue={handleClearAllPending}
          onUnmark={handleUnmark}
          members={members}
          onReassign={handleReassign}
          onChangeTaskType={handleChangeTaskType}
        />
      )}

      {/* Select mode action bar (inline, not floating) */}
      {selectMode && (
        <div className="plan-select-inline-bar">
          <span className="plan-select-count">{selectedIds.size} selected</span>
          <button
            className="btn btn-sm btn-primary"
            disabled={pendingSelectedTasks.length === 0}
            onClick={() => handleCompleteAll(pendingSelectedTasks)}
          >
            Complete {pendingSelectedTasks.length > 0 ? pendingSelectedTasks.length : ''}
          </button>
        </div>
      )}

      {/* Week days */}
      <div className="plan-days">
        {weekDays.map((date, i) => {
          const dayTasks = tasksByDay[date] || [];
          if (dayTasks.length === 0) return null;
          const label = DAY_LABELS[i];
          const isToday = date === todayISO;
          return (
            <DaySection
              key={date}
              date={date}
              label={isToday ? `Today (${label})` : label}
              tasks={dayTasks}
              isOpen={expandedDays.has(date)}
              onToggle={() => setExpandedDays(prev => { const next = new Set(prev); next.has(date) ? next.delete(date) : next.add(date); return next; })}
              selectMode={selectMode}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onSkip={handleSkip}
              onLog={handleLog}
              onDelete={handleDelete}
              onCompleteAll={handleCompleteAll}
              onClearDay={handleClearDay}
              onRescheduleDay={handleRescheduleDay}
              onReschedule={handleReschedule}
              onUnmark={handleUnmark}
              members={members}
              onReassign={handleReassign}
              onChangeTaskType={handleChangeTaskType}
              onReassignDay={handleReassignDay}
              onChangeTypeDay={handleChangeTypeDay}
            />
          );
        })}

        {!loading && overdueTasks.length === 0 && weekDays.every(d => (tasksByDay[d] || []).length === 0) && (
          <div className="plan-empty">
            <Icon name="check" size={32} />
            <p>Nothing scheduled this week.</p>
            <p className="plan-empty-sub">Tap <strong>+</strong> to plan a session or add a task.</p>
          </div>
        )}
      </div>
        </>
      ))}

      {/* Log modal */}
      {view === 'log-task' && logTask && (
        <LogModal
          task={logTask}
          onSave={handleLogSave}
          onCancel={() => { setView('week'); setLogTask(null); }}
          saving={logSaving}
        />
      )}
    </div>
  );
}
